// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the small Markdown the Monday Madness text fields understand. One parser feeds both
// the email renderer and the card, so a to-do ticked in the card looks the same in the mail.
// Supported: # headings, - bullets, 1. numbers, - [ ] / [x] to-dos, **bold**, *italic*,
// ~~strike~~ and bare links. Anything else stays plain text.

export interface MondayInline {
	text: string;
	href?: string;
	bold?: boolean;
	italic?: boolean;
	strike?: boolean;
}

export type MondayBlock =
	| {kind: 'heading'; level: 1 | 2 | 3; inline: Array<MondayInline>}
	/** `line` is the source line, so the card can tick it with `toggleMondayCheck`. */
	| {kind: 'check'; done: boolean; depth: number; line: number; inline: Array<MondayInline>}
	| {kind: 'bullet'; depth: number; inline: Array<MondayInline>}
	| {kind: 'number'; depth: number; label: string; inline: Array<MondayInline>}
	| {kind: 'paragraph'; lines: Array<Array<MondayInline>>}
	| {kind: 'gap'};

const HEADING = /^(#{1,3})\s+(.*)$/;
// "- [ ] x", "* [x] x", "[] x" and "[x] x": the list marker and the space inside are optional.
const CHECK = /^(\s*)(?:[-*+]\s+)?\[( ?|[xX])\](?:\s+(.*))?$/;
const BULLET = /^(\s*)[-*+•]\s+(.*)$/;
const NUMBER = /^(\s*)(\d{1,3})[.)]\s+(.*)$/;
const INLINE =
	/(https?:\/\/[^\s<>"']*[^\s<>"'.,;:!?)])|\*\*([^*\n]+?)\*\*|~~([^~\n]+?)~~|(?<![\w*])\*([^*\s](?:[^*\n]*?[^*\s])?)\*(?![\w*])/g;

function depthOf(indent: string): number {
	return Math.min(3, Math.floor(indent.replace(/\t/g, '  ').length / 2));
}

export function parseMondayInline(text: string): Array<MondayInline> {
	const parts: Array<MondayInline> = [];
	let last = 0;
	for (const match of text.matchAll(INLINE)) {
		if (match.index > last) parts.push({text: text.slice(last, match.index)});
		if (match[1]) parts.push({text: match[1], href: match[1]});
		else if (match[2]) parts.push({text: match[2], bold: true});
		else if (match[3]) parts.push({text: match[3], strike: true});
		else parts.push({text: match[4], italic: true});
		last = match.index + match[0].length;
	}
	if (last < text.length) parts.push({text: text.slice(last)});
	return parts;
}

export function parseMondayMarkdown(text: string): Array<MondayBlock> {
	const blocks: Array<MondayBlock> = [];
	const lines = text.replace(/\r\n?/g, '\n').split('\n');
	lines.forEach((line, index) => {
		const previous = blocks[blocks.length - 1];
		if (!line.trim()) {
			if (previous && previous.kind !== 'gap') blocks.push({kind: 'gap'});
			return;
		}
		const heading = HEADING.exec(line);
		if (heading) {
			blocks.push({
				kind: 'heading',
				level: heading[1].length as 1 | 2 | 3,
				inline: parseMondayInline(heading[2].trim()),
			});
			return;
		}
		const check = CHECK.exec(line);
		if (check) {
			blocks.push({
				kind: 'check',
				done: check[2].toLowerCase() === 'x',
				depth: depthOf(check[1]),
				line: index,
				inline: parseMondayInline(check[3] ?? ''),
			});
			return;
		}
		const bullet = BULLET.exec(line);
		if (bullet) {
			blocks.push({kind: 'bullet', depth: depthOf(bullet[1]), inline: parseMondayInline(bullet[2])});
			return;
		}
		const number = NUMBER.exec(line);
		if (number) {
			blocks.push({
				kind: 'number',
				depth: depthOf(number[1]),
				label: `${number[2]}.`,
				inline: parseMondayInline(number[3]),
			});
			return;
		}
		const inline = parseMondayInline(line.trim());
		if (previous?.kind === 'paragraph') previous.lines.push(inline);
		else blocks.push({kind: 'paragraph', lines: [inline]});
	});
	while (blocks[blocks.length - 1]?.kind === 'gap') blocks.pop();
	return blocks;
}

/** Ticks or unticks the to-do on `line`. Text without a to-do there comes back unchanged. */
export function toggleMondayCheck(text: string, line: number): string {
	const lines = text.split('\n');
	const match = CHECK.exec(lines[line] ?? '');
	if (!match) return text;
	const done = match[2].toLowerCase() === 'x';
	lines[line] = lines[line].replace(/\[( ?|[xX])\]/, done ? '[ ]' : '[x]');
	return lines.join('\n');
}

/**
 * What Enter should add after `line` to carry a list on: the same bullet, the next number, or an
 * unticked to-do. `end` means the line is only a marker, so Enter ends the list instead.
 */
export function continueMondayList(line: string): {kind: 'continue'; prefix: string} | {kind: 'end'} | null {
	const check = /^(\s*)((?:[-*+]\s+)?)\[(?: ?|[xX])\]\s+(.*)$/.exec(line);
	if (check) return check[3].trim() ? {kind: 'continue', prefix: `${check[1]}${check[2]}[ ] `} : {kind: 'end'};
	const bullet = /^(\s*)([-*+•])\s+(.*)$/.exec(line);
	if (bullet) return bullet[3].trim() ? {kind: 'continue', prefix: `${bullet[1]}${bullet[2]} `} : {kind: 'end'};
	const number = /^(\s*)(\d{1,3})([.)])\s+(.*)$/.exec(line);
	if (number) {
		return number[4].trim()
			? {kind: 'continue', prefix: `${number[1]}${Number(number[2]) + 1}${number[3]} `}
			: {kind: 'end'};
	}
	return null;
}
